import { useEffect, useMemo, useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { z } from 'zod'

import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  TemplatesTable,
  formBuilderKeys,
  useCreateTemplate,
  type FormTemplateRow,
  type FormTemplateStatus,
} from '~/features/form-builder'
import { http } from '~/lib/http'
import { isAppError } from '~/lib/result'
import { logger } from '~/lib/logger'
import { useDebouncedValue } from '~/lib/query/use-debounced-value'
import {
  toPaginatedResult,
  usePaginatedQuery,
  type PaginatedParams,
  type PaginatedResult,
} from '~/lib/query/use-paginated-query'
import { useUrlState } from '~/lib/query/use-url-state'

type StatusView = 'all' | FormTemplateStatus

/**
 * `/admin/form-builder` — templates list view (URL-paginated).
 *
 * URL search keys: `page`, `limit`, `search`, `status`, `category`.
 *
 * Falls back to an empty list when the matching admin-api route
 * returns 404 (the route is still being built out per
 * `14-form-builder.md`).
 */
const formBuilderSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(10).max(100).default(25),
  search: z.string().optional(),
  status: z.enum(['all', 'draft', 'published', 'archived']).default('all'),
  category: z.string().optional(),
})
type FormBuilderSearch = z.infer<typeof formBuilderSearchSchema>

const URL_DEFAULTS: FormBuilderSearch = {
  page: 1,
  limit: 25,
  status: 'all',
}

export const Route = createFileRoute('/admin/form-builder/')({
  validateSearch: formBuilderSearchSchema,
  component: FormBuilderListPage,
})

interface TemplatesListPayload {
  readonly templates?: readonly FormTemplateRow[]
  readonly items?: readonly FormTemplateRow[]
  readonly total?: number
  readonly pagination?: {
    readonly page?: number
    readonly limit?: number
    readonly total?: number
    readonly totalPages?: number
  }
}

async function fetchTemplatesPage(
  params: PaginatedParams,
): Promise<PaginatedResult<FormTemplateRow>> {
  const query: Record<string, string | number | undefined> = {
    page: params.page,
    limit: params.limit,
    offset: (params.page - 1) * params.limit,
  }
  if (params.search) query.search = params.search
  if (params.sort) query.sort = params.sort
  const status = params.filters?.status
  if (status && status !== 'all') query.status = status
  if (params.filters?.category) query.category = params.filters.category

  try {
    const raw = await http.get<TemplatesListPayload>(
      '/api/v1/admin/form-builder/templates',
      { query },
    )
    const rows = (raw.templates ?? raw.items ?? []) as readonly FormTemplateRow[]
    const total = raw.pagination?.total ?? raw.total ?? rows.length
    return toPaginatedResult(rows, total, params, raw.pagination)
  } catch (error: unknown) {
    if (isAppError(error) && error.code === 'not_found') {
      return toPaginatedResult<FormTemplateRow>([], 0, params)
    }
    throw error
  }
}

function FormBuilderListPage(): ReactElement {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [, setUrl] = useUrlState(formBuilderSearchSchema, URL_DEFAULTS)

  const [searchInput, setSearchInput] = useState<string>(search.search ?? '')
  const debouncedSearch = useDebouncedValue(searchInput, 300)
  useEffect(() => {
    const normalized =
      debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim()
    if (normalized !== search.search) {
      setUrl({ search: normalized, page: 1 })
    }
  }, [debouncedSearch, search.search, setUrl])

  const params: PaginatedParams = {
    page: search.page,
    limit: search.limit,
    search: search.search,
    filters: {
      status: search.status,
      ...(search.category ? { category: search.category } : {}),
    },
  }

  const templatesList = usePaginatedQuery<FormTemplateRow>({
    queryKey: [...formBuilderKeys.all, 'list-paginated'],
    params,
    fetcher: fetchTemplatesPage,
  })
  const createMutation = useCreateTemplate()

  const templates = useMemo(
    () => templatesList.data?.data ?? [],
    [templatesList.data],
  )
  const pagination = templatesList.data?.pagination
  const counts = useMemo(() => {
    const all = templates
    return {
      total: pagination?.total ?? all.length,
      drafts: all.filter((t) => t.status === 'draft').length,
    }
  }, [templates, pagination])

  const hasFilters =
    searchInput.length > 0 ||
    search.status !== 'all' ||
    (search.category ?? '').length > 0

  const clearFilters = (): void => {
    setSearchInput('')
    setUrl({
      search: undefined,
      status: 'all',
      category: undefined,
      page: 1,
    })
  }

  const openEditor = (template: FormTemplateRow): void => {
    void navigate({
      to: '/admin/form-builder/$templateId',
      params: { templateId: template.id },
    })
  }

  const handleCreate = (): void => {
    const stamp = Date.now()
    const slug = `untitled-${stamp.toString(36)}`
    createMutation.mutate(
      { name: 'Untitled template', slug },
      {
        onSuccess: (created) => {
          void navigate({
            to: '/admin/form-builder/$templateId',
            params: { templateId: created.id },
          })
        },
        onError: (cause) =>
          logger.warn('form-builder create failed', { cause }),
      },
    )
  }

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Form Builder</h1>
          <p className="ka-page-header-subtitle">
            {counts.total} templates &middot; {counts.drafts} drafts
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--primary"
            onClick={handleCreate}
            disabled={createMutation.isPending}
          >
            <Plus size={14} aria-hidden="true" /> New Template
          </button>
        </div>
      </header>

      <FilterStrip
        search={searchInput}
        view={search.status}
        category={search.category ?? ''}
        onSearch={setSearchInput}
        onView={(value) => setUrl({ status: value, page: 1 })}
        onCategory={(value) =>
          setUrl({
            category: value === '' ? undefined : value,
            page: 1,
          })
        }
      />

      <TemplatesTable
        templates={templates}
        isLoading={templatesList.isPending}
        error={templatesList.error}
        hasFilters={hasFilters}
        onRowClick={openEditor}
        onClearFilters={clearFilters}
        onCreateTemplate={handleCreate}
      />
      <DataTablePagination
        page={pagination?.page ?? search.page}
        limit={pagination?.limit ?? search.limit}
        total={pagination?.total ?? templates.length}
        isLoading={templatesList.isPending}
        onChange={({ page, limit }) => setUrl({ page, limit })}
      />
    </div>
  )
}

interface FilterStripProps {
  readonly search: string
  readonly view: StatusView
  readonly category: string
  readonly onSearch: (value: string) => void
  readonly onView: (value: StatusView) => void
  readonly onCategory: (value: string) => void
}

const STATUS_CHIPS: ReadonlyArray<{ readonly id: StatusView; readonly label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Drafts' },
  { id: 'published', label: 'Published' },
  { id: 'archived', label: 'Archived' },
]

const CATEGORY_CHIPS: ReadonlyArray<string> = [
  'Sales',
  'Support',
  'HR',
  'Marketing',
  'Product',
  'Finance',
]

function FilterStrip({
  search,
  view,
  category,
  onSearch,
  onView,
  onCategory,
}: FilterStripProps): ReactElement {
  return (
    <div className="ka-filter-strip">
      <div className="ka-filter-group">
        <span className="ka-filter-group-label">Status</span>
        {STATUS_CHIPS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`ka-detail-chip${view === chip.id ? ' is-active' : ''}`}
            onClick={() => onView(chip.id)}
            aria-pressed={view === chip.id}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="ka-filter-group">
        <span className="ka-filter-group-label">Category</span>
        <button
          type="button"
          className={`ka-detail-chip${category === '' ? ' is-active' : ''}`}
          onClick={() => onCategory('')}
          aria-pressed={category === ''}
        >
          Any
        </button>
        {CATEGORY_CHIPS.map((cat) => (
          <button
            key={cat}
            type="button"
            className={`ka-detail-chip${category === cat ? ' is-active' : ''}`}
            onClick={() => onCategory(cat)}
            aria-pressed={category === cat}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="ka-filter-search">
        <input
          type="search"
          className="ka-field-input"
          placeholder="Search templates"
          aria-label="Search templates"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
    </div>
  )
}
